'use client';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { Button, Tabs, TabsList, TabsTrigger, useHorizontalScroller } from 'ui';

export const TabsSwitcher = ({
  tabs,
  prefix,
}: {
  prefix: string;
  tabs: {
    path: string;
    label: string;
  }[];
}) => {
  const path = usePathname();

  // Arrows replace the horizontal scrollbar, which ate vertical space under the
  // tabs. They only render when the list actually overflows.
  const scroller = useHorizontalScroller<HTMLDivElement>(tabs.length);

  const { scrollElementIntoView } = scroller;

  const tab = useMemo(() => {
    return Array.from(tabs)
      .sort((a) => (!!a.path ? -1 : 1))
      .find((tab) => path.includes(tab.path))?.path;
  }, [path]);

  // The active tab can sit outside the visible window on load; without this it
  // would stay hidden until the user scrolls looking for it.
  useEffect(() => {
    scrollElementIntoView(
      scroller.ref.current?.querySelector<HTMLElement>(
        '[data-state="active"]',
      ) ?? null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, scrollElementIntoView]);

  return (
    <Tabs value={tab}>
      <div className='flex flex-row items-center gap-1'>
        {scroller.hasOverflow && (
          <Button
            type='button'
            variant='ghost'
            size='xs'
            className='shrink-0 px-1 mr-1'
            aria-label='Ver pestañas anteriores'
            disabled={!scroller.canScrollLeft}
            onClick={scroller.scrollLeft}
          >
            <IconChevronLeft className='h-4 w-4' />
          </Button>
        )}

        <TabsList
          ref={scroller.ref}
          onScroll={scroller.syncScrollState}
          // The scrollbar itself stays hidden: the arrows are the affordance.
          style={{ scrollbarWidth: 'none' }}
          className='h-auto min-w-0 max-w-full justify-start overflow-x-auto [&::-webkit-scrollbar]:hidden'
        >
          {tabs.map((tab) => {
            return (
              <Link
                key={tab.path}
                href={`${prefix}/${tab.path}`}
                className='shrink-0'
              >
                <TabsTrigger value={tab.path} className='px-3'>
                  {tab.label}
                </TabsTrigger>
              </Link>
            );
          })}
        </TabsList>

        {scroller.hasOverflow && (
          <Button
            type='button'
            variant='ghost'
            size='xs'
            className='shrink-0 px-1 ml-1'
            aria-label='Ver pestañas siguientes'
            disabled={!scroller.canScrollRight}
            onClick={scroller.scrollRight}
          >
            <IconChevronRight className='h-4 w-4' />
          </Button>
        )}
      </div>
    </Tabs>
  );
};
